'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useAtom } from 'jotai'
import { useTranslations } from 'next-intl'
import { useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import z from 'zod'

import { SubmitButton } from '@/components/submit-button'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp'
import { useAuth, useAuthDialogControl } from '@/features/auth/hooks'
import { useResetClientStorage } from '@/features/auth/hooks/use-reset-client-storage'
import { navigateAfterAuth } from '@/features/auth/lib/post-auth-navigation'
import { toast } from '@/lib/toast'
import { useResendVerificationEmail, useVerifyEmail } from '@/service/auth.hook'
import { emailResendAtom } from '@/state/auth'

const verifySchema = z.object({
  code: z
    .string()
    .regex(/^\d{6}$/, 'Code must be exactly 6 digits')
    .min(6, 'Code must be exactly 6 digits')
    .max(6, 'Code must be exactly 6 digits'),
})

type VerifyFormData = z.infer<typeof verifySchema>

export function EmailVerifyForm() {
  const authT = useTranslations('auth')
  const commonT = useTranslations('common')
  const searchParams = useSearchParams()
  const callbackUrl = searchParams.get('callbackUrl') || '/login'
  const router = useRouter()
  const form = useForm<VerifyFormData>({
    resolver: zodResolver(verifySchema),
    mode: 'onChange',
  })
  const { accessToken, user, updateEmailVerification, logout, loginWithEmail } = useAuth()
  const resetClientStorage = useResetClientStorage()
  const [emailResend, setEmailResend] = useAtom(emailResendAtom)
  const canLogin = Boolean(emailResend.email) && Boolean(emailResend.password)
  const [remaining, setRemaining] = useState(0)
  const [verifyError, setVerifyError] = useState<string | null>(null)
  const { isOpen, handleSuccess } = useAuthDialogControl()

  const handleAuthComplete = () => {
    if (isOpen) {
      handleSuccess()
    } else {
      navigateAfterAuth(callbackUrl)
    }
  }
  const { mutateAsync: resendVerificationEmail } = useResendVerificationEmail({
    onSuccess: (data) => {
      if (data.success) {
        setEmailResend({ ...emailResend, cooldown: Date.now() + 60 * 1000 })
        toast.success(authT('verificationEmailSent'))
      }
    },
  })
  const { mutateAsync: verifyEmail } = useVerifyEmail({
    onSuccess: async (data) => {
      toast.success(data.message)
      if (data.success && canLogin && accessToken === null) {
        // We already have the credentials the user just signed up with —
        // log them in straight away instead of bouncing to /login and making
        // them retype the password. Fall back to a plain /login redirect
        // (prefilled with the email) if the auto-login itself fails for any
        // reason, so verification success never leaves the user stranded.
        try {
          await loginWithEmail({ email: emailResend.email, password: emailResend.password! })
          resetClientStorage()
          handleAuthComplete()
        } catch (error) {
          resetClientStorage()

          const params = new URLSearchParams({ email: emailResend.email })
          const rawCallbackUrl = searchParams.get('callbackUrl')
          if (rawCallbackUrl) params.set('callbackUrl', rawCallbackUrl)
          router.replace(`/login?${params.toString()}`)
        }
      } else if (accessToken) {
        // Already logged in but unverified. updateEmailVerification clears the
        // cookie's verification flag AND refreshes the access token to a verified
        // one — authed calls gate on the token's claim, so without the refresh the
        // dashboard's account check would reject with "verify your email" and
        // strand the user. Await it so the fresh token lands before we navigate.
        const refreshed = await updateEmailVerification(true)
        if (!refreshed) {
          logout()
          return
        }
        handleAuthComplete()
      } else {
        router.replace('/login')
      }
    },
  })

  const onSubmit = async (data: VerifyFormData) => {
    setVerifyError(null)
    try {
      await verifyEmail({
        email: emailResend.email,
        verificationCode: data.code,
      })
    } catch (error) {
      // The auto-submit path (typing the 6th digit) calls this without awaiting, so a
      // rejected mutation would otherwise surface as an unhandled promise rejection.
      // Catch it, clear the input for another attempt, and show the reason inline.
      const message =
        (error as { message?: string })?.message ?? authT('invalidOrExpiredVerificationCode')
      setVerifyError(message)
      form.setValue('code', '')
    }
  }

  const handleOnClickResendVerificationEmail = async () => {
    if (!user?.email && !emailResend.email) {
      toast.error(authT('cannotFindEmail'))
      router.push('/login')
      return
    }

    setVerifyError(null)
    try {
      await resendVerificationEmail({
        email: (user?.email || emailResend.email) ?? '',
      })
    } catch (error) {
      const message =
        (error as { message?: string })?.message ?? authT('couldNotResendVerificationEmail')
      setVerifyError(message)
    }
  }

  useEffect(() => {
    if (remaining <= 0) return

    const interval = setInterval(() => {
      setRemaining(Math.max(0, emailResend.cooldown - Date.now()))
    }, 1000)
    return () => clearInterval(interval)
  }, [emailResend.cooldown, remaining])

  return (
    <form
      className="flex w-full flex-col gap-6 px-3.5"
      onSubmit={form.handleSubmit(onSubmit)}
      data-1p-ignore={false}
    >
      <p className="text-center text-sm tracking-tight">
        {authT.rich('codeHasBeenSentTo', {
          email: () => <b className="font-medium break-all">{emailResend.email}</b>,
        })}
      </p>
      <p className="text-center text-xl tracking-tight">{authT('enterCode')}</p>
      <InputOTP
        maxLength={6}
        value={form.watch('code')}
        onChange={(value) => {
          form.setValue('code', value)
          if (verifyError) setVerifyError(null)
          if (value.length === 6) {
            onSubmit({ code: value })
          }
        }}
        data-1p-ignore={false}
      >
        <div className="mx-auto flex items-center gap-2">
          <InputOTPGroup className="gap-2" data-1p-ignore={false}>
            <InputOTPSlot className="w-8" index={0} />
            <InputOTPSlot className="w-8 border-l" index={1} />
            <InputOTPSlot className="w-8 border-l" index={2} />
            <InputOTPSlot className="w-8 border-l" index={3} />
            <InputOTPSlot className="w-8 border-l" index={4} />
            <InputOTPSlot className="w-8 border-l" index={5} />
          </InputOTPGroup>
        </div>
      </InputOTP>
      {verifyError && (
        <p className="text-destructive text-center text-sm tracking-tight" role="alert">
          {verifyError}
        </p>
      )}
      <SubmitButton
        className="mx-auto w-[calc(100%-64px)]"
        isSuccessful={form.formState.isSubmitting}
      >
        {commonT('continue')}
      </SubmitButton>
      <button
        className="cursor-pointer text-sm tracking-tight"
        onClick={async (e) => {
          e.preventDefault()
          await handleOnClickResendVerificationEmail()
        }}
      >
        {remaining > 0
          ? authT('resendAfter', { seconds: Math.ceil(remaining / 1000) })
          : authT('resendVerificationEmail')}
      </button>
    </form>
  )
}
